import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    Alert,
    CircularProgress,
    Grid,
    Box,
    Card,
    CardContent,
    Typography
} from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { updateCompany, getCompanyUsers } from '../../Utils/accountApi';
import { useTranslation } from '../../hooks/useTranslation';

export default function CompanyTab() {
    const {
        currentClient,
        currentUser,
        canEditCompanyInfo,
        canManageUsers,
        isCompanyUser,
        loading: permissionsLoading,
        refreshUserData
    } = usePermissions();
    const t = useTranslation('Company.json');
    const tCommon = useTranslation('Common.json');

    const [companyData, setCompanyData] = useState({
        raison_sociale: ''
    });
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (currentClient) {
            setCompanyData({
                raison_sociale: currentClient.raison_sociale || ''
            });

            if (isCompanyUser && canManageUsers) {
                loadUsers();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentClient, isCompanyUser, canManageUsers]);

    const loadUsers = async () => {
        try {
            const userData = await getCompanyUsers();
            setUsers(userData);
        } catch (err) {
            console.error('Erreur lors du chargement des utilisateurs:', err);
        }
    };

    const handleCompanyChange = (e) => {
        const { name, value } = e.target;
        setCompanyData(prev => ({
            ...prev,
            [name]: value
        }));
        setSuccess(false);
        setError(null);
    };

    const handleCompanySubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            await updateCompany(companyData);
            await refreshUserData();
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error('Erreur lors de la mise à jour de l\'entreprise:', err);
            setError(err.response?.data?.error || t('company.error.generic'));
        } finally {
            setLoading(false);
        }
    };

    if (permissionsLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress sx={{ color: '#FF7233' }} />
            </Box>
        );
    }

    if (!isCompanyUser) {
        return (
            <Alert severity="info">
                {t('company.restricted.section')}
            </Alert>
        );
    }

    if (!canEditCompanyInfo && !canManageUsers) {
        return (
            <Alert severity="warning">
                {t('company.no_permission')}
            </Alert>
        );
    }

    return (
        <Box>
            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {t('company.success')}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {canEditCompanyInfo && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('company.tab.info')}
                    </Typography>
                    <form onSubmit={handleCompanySubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('company.field.legal_name')}
                                    name="raison_sociale"
                                    value={companyData.raison_sociale}
                                    onChange={handleCompanyChange}
                                    disabled={loading}
                                    size="small"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={loading}
                                        sx={{
                                            bgcolor: '#FF7233',
                                            '&:hover': {
                                                bgcolor: '#e05a1a'
                                            }
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} /> : tCommon('common.save')}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            )}

            {canManageUsers && (
                <Box>
                    <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                        {t('company.users_title')}
                    </Typography>

                    {users.length === 0 ? (
                        <Alert severity="info">{t('company.no_users')}</Alert>
                    ) : (
                        <Grid container spacing={2}>
                            {users.map((user) => (
                                <Grid item xs={12} key={user.id_utilisateur}>
                                    <Card variant="outlined">
                                        <CardContent sx={{ py: 1.5 }}>
                                            <Typography variant="subtitle1">
                                                {user.prenom} {user.nom}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {user.email}
                                            </Typography>
                                            <Typography variant="caption">
                                                {user.is_administrator_account ? (
                                                    <strong style={{ color: '#FF7233' }}>{t('company.role.admin')}</strong>
                                                ) : (
                                                    t('company.role.user')
                                                )}
                                                {user.id_utilisateur === currentUser?.id_utilisateur && (
                                                    <span style={{ marginLeft: 8, color: '#FF7233' }}>({t('company.you')})</span>
                                                )}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            )}
        </Box>
    );
}
